import formatMoneyValueOnlyDigits from "components/utils/formatMoneyValueOnlyDigits";
import getYear from "date-fns/getYear";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import parseDateYmd from "lib/utils/parseDateYmd";
import last from "lodash/fp/last";
import Sparkline from "components/charts/modern/SparklineChart";

interface ITotalCompensationsSummaryProps {
  totalCompensations: IBrazilianCompanyIssuerTopLevelFragment["totalCompensations"];
  financials: IBrazilianCompanyIssuerTopLevelFragment["financials"];
}

export default function TotalCompensationsSummary({ totalCompensations, financials }: ITotalCompensationsSummaryProps) {
  const { yearly } = financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0;

  const topCompensation = last(
    [...totalCompensations]
      .sort((a, b) => {
        const aFiscalYear = getYear(parseDateYmd(a.fiscalYearEnd));
        const bFiscalYear = getYear(parseDateYmd(b.fiscalYearEnd));
        return aFiscalYear - bFiscalYear;
      })
      .filter((compensation) => {
        const compensationFiscalYearEnd = getYear(parseDateYmd(compensation.fiscalYearEnd));
        if (isBank) {
          return yearlyBankFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }
        if (isCompany) {
          return yearlyCompanyFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }
        if (isInsurer) {
          return yearlyInsurerFinancials.some(
            ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
          );
        }

        return false;
      }),
  );

  if (!topCompensation) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
      <div>
        <h1 className="m-0 text-xl font-bold tracking-wider text-slate-800">
          R${formatMoneyValueOnlyDigits(topCompensation.totalCompensation)}
        </h1>

        <span className="text-sm font-medium text-slate-500">
          Total (reais) • {getYear(parseDateYmd(topCompensation.fiscalYearEnd))}
        </span>
      </div>

      {totalCompensations.length >= 3 && (
        <div>
          <Sparkline
            type="area"
            className="h-14 w-full min-w-0 sm:max-w-xs"
            accessibilityDescription="Gráfico demonstrando a evolução do total gasto em Remuneração dos Administradores"
            data={totalCompensations.map((node) => {
              return {
                y: node.totalCompensation,
              };
            })}
          />
        </div>
      )}
    </div>
  );
}
